import type { ButtonHTMLAttributes } from 'react';

// The portal's ROW-ACTION pill (s49, canonicalized from the ad-hoc copies in Clients.tsx /
// ClientServicesPanel.tsx; extended n39, 04 #39). Fully rounded (--pill-radius), bordered,
// quiet until hovered. Use for in-row / in-card actions (Edit, Delete, Manage, Stop); keep
// the shared Button for block/form actions and SegmentedSelect for exclusive choice.
//
// TONES are the s51 semantic ladder and are MEANING, not styling (the s104 lesson: never
// flatten distinct tones because they look similar):
//   green = money-in / positive (Record payment, Confirm, Resume)
//   red = destructive (Void, Delete; per the n39 fork-E rule a destructive FIRST button
//         stays neutral when a danger-confirm dialog follows, and is red only when it
//         acts immediately with no confirmation step)
//   amber = pause / caution (Pause, the reconcile prompts)
//   blue = navigational document chip (deep links carrying a document number)
//   neutral = ordinary row actions and navigation
// SIZES: md is the shipped pill; xs is the QuickTimer mini-chip scale; lg is the picker
// and list-row scale (the former px-4 py-1.5 text-sm ad-hoc pills).
// GLOW: the QuickTimer status glows, driven by the --pill-glow-* theme tokens (a flat
// theme sets them to the empty shadow).
export type PillTone = 'neutral' | 'red' | 'green' | 'amber' | 'blue';
export type PillSize = 'md' | 'xs' | 'lg';
export type PillGlow = 'green' | 'green-flash' | 'red' | 'red-flash';

const toneClasses: Record<PillTone, string> = {
  neutral: 'control-glass border-[rgb(var(--edge-rgb)/0.15)] text-[var(--color-text)] hover:bg-[rgb(var(--edge-rgb)/0.05)]',
  red: 'border-[var(--tint-red-border)] bg-[var(--tint-red-bg-soft)] text-[var(--tint-red-text)] hover:bg-[var(--tint-red-bg)]',
  green: 'border-[var(--tint-green-border)] bg-[var(--tint-green-bg-soft)] text-[var(--tint-green-text)] hover:bg-[var(--tint-green-bg)]',
  amber: 'border-[var(--tint-warn-border)] bg-[var(--tint-warn-bg-soft)] text-[var(--tint-warn-text)] hover:bg-[var(--tint-warn-bg)]', // s51: the Pause action (yellow)
  blue: 'border-[var(--tint-blue-border)] bg-[var(--tint-blue-bg-soft)] text-[var(--tint-blue-text)] hover:bg-[var(--tint-blue-bg)]',
};

const sizeClasses: Record<PillSize, string> = {
  md: 'px-3 py-1 text-xs',
  xs: 'px-2 py-0.5 text-xs',
  lg: 'px-4 py-1.5 text-sm',
};

const glowClasses: Record<PillGlow, string> = {
  green: 'shadow-[var(--pill-glow-green)]',
  'green-flash': 'shadow-[var(--pill-glow-green-flash)]',
  red: 'shadow-[var(--pill-glow-red)]',
  'red-flash': 'shadow-[var(--pill-glow-red-flash)]',
};

interface PillButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: PillTone;
  size?: PillSize;
  glow?: PillGlow;
}

/**
 * The full visual class string for a pill tone + size (+ glow), WITHOUT the disabled
 * state classes the component adds. Exported so an anchor or router Link can wear
 * exactly the pill treatment when its correct element is <a> (n39 layer 2b) — one
 * source of truth, zero drift. The component itself composes through this builder.
 */
export function pillClasses(
  tone: PillTone = 'neutral',
  size: PillSize = 'md',
  glow?: PillGlow,
  className = ''
): string {
  return [
    'inline-flex shrink-0 items-center justify-center rounded-[var(--pill-radius)] border control-weight transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-accent)]',
    toneClasses[tone],
    sizeClasses[size],
    glow ? glowClasses[glow] : '',
    className,
  ].join(' ');
}

export function PillButton({ tone = 'neutral', size = 'md', glow, className = '', children, ...rest }: PillButtonProps): JSX.Element {
  return (
    <button
      type="button"
      className={[
        pillClasses(tone, size, glow, className),
        'disabled:cursor-not-allowed disabled:opacity-50',
      ].join(' ')}
      {...rest}
    >
      {children}
    </button>
  );
}
